import { createTheme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { alpha } from '@mui/material';
import { Shadows } from '@mui/material/styles/shadows';

/**
 * The primary Fixtuur branding theme, any tenant themes should override this where absolutely necessary
 */
const fixtuur = createTheme({
    palette: {
        primary: {
            main: '#FB3569',
            dark: '#C2003F',
            light: '#FF7097',
            contrastText: '#FFFFFF',
        },
        secondary: {
            main: '#272838',
            dark: '#000013',
            light: '#676773',
            contrastText: '#FFFFFF',
        },
        background: { paper: '#FAFAFA', default: '#ffffff' },
        error: {
            main: '#F44336',
            dark: '#E31B0C',
            light: '#F88078',
            contrastText: '#FFFFFF',
        },
        info: {
            main: '#2196F3',
            dark: '#2196F3',
            light: '#2196F3',
            contrastText: '#FFFFFF',
        },
        warning: {
            main: '#ED6C02',
            dark: '#C77700',
            light: '#C77700',
            contrastText: '#FFFFFF',
        },
        success: {
            main: '#4CAF50',
            dark: '#3B873E',
            light: '#3B873E',
            contrastText: '#FFFFFF',
        },
        text: {
            primary: alpha('#040406DE', 0.87),
            secondary: alpha('#040406DE', 0.6),
            disabled: alpha('#040406DE', 0.38),
        },
        divider: 'rgba(4, 4, 6, 0.12)',
    },
    typography: {
        fontFamily: 'Inter',
        fontSize: 16,
        h1: { fontSize: '3rem', fontWeight: 600, lineHeight: '7rem' },
        h2: { fontSize: '2.0625rem', fontWeight: 600, lineHeight: '2.5rem' },
        h3: { fontSize: '1.4375rem', fontWeight: 600, lineHeight: '1.6875rem' },
        h4: { fontSize: '1.1875rem', fontWeight: 600, lineHeight: '1.4375rem' },
        h5: { fontSize: '1.000rem', fontWeight: 600, lineHeight: '1.3125rem' },
        h6: { fontSize: '0.8125rem', fontWeight: 600, lineHeight: '1.3125rem' },
        body1: { fontSize: '0.75rem', fontWeight: 400, lineHeight: '1.125rem' },
        body2: { fontSize: '0.6875rem', fontWeight: 400, lineHeight: '1rem' },
        caption: {
            fontSize: '0.75rem',
        },
        subtitle2: {
            fontSize: '0.875rem',
        },
    },
    zIndex: {
        appBar: 1200,
        // TODO: sort out drawer zindex - make all drawers use this and make variables for on top
        // of or beneath the app bar accordingly
        drawer: 5,
    },
    shadows: Array(25).fill('none') as Shadows,
    components: {
        /* WARNING: Global styling should be a last resort, default to creating custom styled
         * components and reusing them throughout the app.
         */
        MuiTab: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                },
            },
        },
        MuiFab: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                },
                primary: ({ theme }) => ({
                    '&:hover': {
                        backgroundColor: theme.palette.primary.dark,
                    },
                }),
                secondary: ({ theme }) => ({
                    backgroundColor: theme.palette.background.default,
                    color: theme.palette.secondary.main,
                    '&:hover': {
                        color: theme.palette.primary.main,
                        backgroundColor: theme.palette.background.default,
                    },
                }),
            },
        },
        MuiIconButton: {
            styleOverrides: {
                root: ({ theme }) => ({
                    '&:hover': {
                        color: theme.palette.primary.main,
                    },
                }),
            },
        },
    },
});

export const useTabStyles = makeStyles({
    root: {
        justifyContent: 'center',
        alignItems: 'center',
    },
    scroller: {
        flexGrow: '0',
    },
    indicatorLoading: {
        backgroundColor: fixtuur.palette.grey[200],
    },
    hideIndicator: {
        display: 'none',
    },
    removeTextTransform: {
        textTransform: 'none',
    },
});

/**
 * Moda override
 */
const moda = createTheme(fixtuur, {
    palette: {
        primary: {
            main: '#5A7592',
            dark: '#3E5166',
            light: '#7B90A7',
        },
        secondary: {
            main: '#272838',
            dark: '#141413',
            light: '#4a4a49',
        },
    },
});

/**
 * Constructs a new theme object with overrides for specific tenants.
 * Should really only be used during initialisation, to access the theme at component level, use
 * instead the useTheme hook from '@mui/material'.
 * @param tenant string tenant name
 * @returns Theme
 */
export const getTheme = (tenant?: string) => {
    if (tenant === 'moda') {
        return moda;
    } else return fixtuur;
};
